import { Injectable } from '@angular/core';
import * as InteractJS from 'interactjs/dist/interact.js';
import { RubricHTMLElement } from '../model/rubric-HTMLElement';
import { RubricStamp } from '../model/rubric-stamp';
import { SignatoryStamp } from '../model/signatory-stamp';
import { FormCustomField } from '../model/form-custom-field';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';
import { BrowserUtil } from 'src/app/util/browser-util';
import {
  calculatePlacementValidity,
  PlacementElementType,
  PlacementValidationElement
} from './placement-validation-engine';

let stampParams: Array<SignatoryStamp> = [];
let stampParamsDefault: Array<SignatoryStamp> = [];
let rubricParams: Array<RubricStamp> = [];
let rubricParamsDefault: Array<RubricStamp> = [];
let customFieldParams: Array<FormCustomField> = [];
let rubricElements = [];
let validStep = true;

let _includeRubrics = false;
let _fileName = '';

@Injectable({
  providedIn: 'root',
})
export class InteractService {
  canResize: boolean = !BrowserUtil.isMobile();
  private _isOrdered = false;
  private _lockDrag = false;
  private _lockRubric = false;
  private _withRubrics = false;
  private _loadSignature = false;
  private _listSignatureLines: Array<any> = [];
  private _listRubrics: RubricStamp[] = [];

  constructor() { }

  get rubricElements(): Array<RubricHTMLElement> {
    return rubricElements;
  }

  get validStep(): boolean {
    return validStep;
  }

  set validStep(valid: boolean) {
    validStep = valid;
  }

  get signatoryStamps(): Array<SignatoryStamp> {
    return stampParams;
  }

  get customFields(): Array<FormCustomField> {
    return customFieldParams;
  }

  set signatoryStamps(stamps: Array<SignatoryStamp>) {
    stampParams = stamps;
  }

  set addSignatoryStamp(signatory: SignatoryStamp) {
    stampParams.push(signatory);
  }

  set addRubricStamp(rubric: RubricStamp) {
    rubricParams.push(rubric);
  }

  set addCustomField(customField: FormCustomField) {
    customFieldParams.push(customField);
  }

  get rubricStamps(): Array<RubricStamp> {
    return rubricParams;
  }

  get isOrdered(): boolean {
    return this._isOrdered;
  }

  set isOrdered(value: boolean) {
    this._isOrdered = value;
  }

  get fileName(): string {
    return _fileName;
  }

  set fileName(value: string) {
    _fileName = value;
  }

  get lockDrag(): boolean {
    return this._lockDrag;
  }

  set lockDrag(value: boolean) {
    this._lockDrag = value;
  }

  get includeRubrics(): boolean {
    return _includeRubrics;
  }

  set includeRubrics(value: boolean) {
    _includeRubrics = value;
  }

  get lockRubric(): boolean {
    return this._lockRubric;
  }

  set lockRubric(value: boolean) {
    this._lockRubric = value;
  }

  get listSignatureLines(): Array<any> {
    return this._listSignatureLines;
  }

  set listSignatureLines(value: Array<any>) {
    this._listSignatureLines = value;
  }

  get listRubrics(): RubricStamp[] {
    return this._listRubrics;
  }

  set listRubrics(value: RubricStamp[]) {
    this._listRubrics = value;
  }

  get withRubrics(): boolean {
    return this._withRubrics;
  }

  set withRubrics(value: boolean) {
    this._withRubrics = value;
  }

  get loadSignature(): boolean {
    return this._loadSignature;
  }

  set loadSignature(value: boolean) {
    this._loadSignature = value;
  }

  public emptyStampRubricParams() {
    stampParams = [];
    stampParamsDefault = [];
    rubricParams = [];
    rubricParamsDefault = [];
    customFieldParams = [];
    rubricElements = [];
    this.lockDrag = false;
    this.lockRubric = false;
    this._listRubrics = [];
  }

  public emptySignatureAndRubricElements() {
    rubricElements = [];
    rubricParams = [];
    rubricParamsDefault = [];
    this._listRubrics = [];
  }

  startInteract(classNameDraggable: string, classNameDropzone: string) {
    if (!BrowserUtil.isMobile()) {
      this.loadingDraggable(classNameDraggable);
    }
    this.loadingDropzone(classNameDropzone);
  }

  stopInteract(className: string) {
    InteractJS(`.${className}`).unset();
  }

  restoreInitialPosition(classDraggable: string) {
    const signatures = document.getElementsByClassName(classDraggable);
    for (let index = 0; index < signatures.length; index++) {
      const element = signatures[index] as HTMLElement;
      element.style.transform = 'translate(0px, 0px)';
      element.setAttribute('data-x', '0');
      element.setAttribute('data-y', '0');
      element.setAttribute('out-document', 'false');
      element.setAttribute('valid', 'true');
      element.classList.remove('cannot-drop');
      element.classList.remove('cannot-drop-out-document');
    }
    stampParams = JSON.parse(JSON.stringify(stampParamsDefault)) as SignatoryStamp[];
    validStep = this.isValidStep();
  }

  restoreInitialRubricPosition(classDraggable: string) {
    const rubrics = document.getElementsByClassName(classDraggable);
    for (let index = 0; index < rubrics.length; index++) {
      const element = rubrics[index] as HTMLElement;
      element.style.transform = 'translate(0px, 0px)';
      element.setAttribute('data-x', '0');
      element.setAttribute('data-y', '0');
      element.setAttribute('out-document', 'false');
      element.setAttribute('valid', 'true');
      element.classList.remove('cannot-drop');
      element.classList.remove('cannot-drop-out-document');
    }
    rubricParams = JSON.parse(JSON.stringify(rubricParamsDefault)) as RubricStamp[];
    validStep = this.isValidStep();
  }

  isValidStep() {
    return stampParams.every((x) => x.invalid === false)
      && rubricParams.every((x) => x.invalid === false)
      && customFieldParams.every((x) => x.invalid === false);
  }

  initStampParams(signatoryStamp: any) {
    const index = stampParams.indexOf(
      stampParams.find((x) => x.signatoryId === signatoryStamp.signatoryId && x.fileName === signatoryStamp.fileName)
    );
    if (index >= 0) {
      stampParams[index].signaturePositionPage = signatoryStamp.signaturePositionPage;
      stampParams[index].signaturePositionX = signatoryStamp.signaturePositionX;
      stampParams[index].signaturePositionY = signatoryStamp.signaturePositionY;
    } else {
      stampParams.push(signatoryStamp);
    }
    stampParamsDefault = JSON.parse(JSON.stringify(stampParams)) as SignatoryStamp[];
  }

  initRubricParams(signatoryRubricStamp: any) {
    const index = rubricParams.indexOf(
      rubricParams.find((x) => x.signatoryId === signatoryRubricStamp.signatoryId && x.gId === signatoryRubricStamp.gId)
    );
    if (index >= 0) {
      rubricParams[index].page = signatoryRubricStamp.page;
      rubricParams[index].positionX = signatoryRubricStamp.positionX;
      rubricParams[index].positionY = signatoryRubricStamp.positionY;
    } else {
      rubricParams.push(signatoryRubricStamp);
    }
    rubricParamsDefault = JSON.parse(JSON.stringify(rubricParams)) as RubricStamp[];
  }


  initRubricElements(rubric: RubricHTMLElement) {
    const index = rubricElements.indexOf(rubricElements
      .find(x => x.fileName === rubric.fileName && x.page === rubric.rubrics.id));
    if (index >= 0) {
      rubricElements[index].rubricSection = rubric.rubricSection;
      rubricElements[index].rubrics = rubric.rubrics;
    } else {
      rubricElements.push(rubric);
    }
  }

  findFileNameToUpdate(value) {
    return value.fileName === this;
  }

  deleteStampParamsDefault() {
    stampParams = stampParams.filter(x => x.default === false);
    validStep = this.isValidStep();
  }

  deleteRubricParamsDefault() {
    rubricParams = rubricParams.filter(x => x.default === false);
    validStep = this.isValidStep();
  }

  deleteStampParams(fileName: string) {
    stampParams = stampParams.filter(x => x.fileName !== fileName);
    validStep = this.isValidStep();
  }

  deleteAllStampParams() {
    stampParams = [];
    validStep = this.isValidStep();
  }

  deleteSingleStampParams(gid: string) {
    stampParams = stampParams.filter(x => x.gId !== gid);
    validStep = this.isValidStep();
  }

  deleteSingleRubricParams(gid: string) {
    rubricParams = rubricParams.filter(x => x.gId !== gid);
    validStep = this.isValidStep();
  }

  deleteSingleCustomFieldParams(gid: string) {
    customFieldParams = customFieldParams.filter((x) => x.gId !== gid);
    validStep = this.isValidStep();
  }

  validParams() {
    validStep = this.isValidStep();
  }

  deleteRubricsParams(fileName: string) {
    rubricParams = rubricParams.filter(x => x.fileName !== fileName);
  }

  deleteAllRubricsParams() {
    rubricParams = [];
  }

  deleteCustomFieldsParams(fileName: string) {
    customFieldParams = customFieldParams.filter((x) => x.fileName !== fileName);
    validStep = this.isValidStep();
  }

  deleteAllCustomFieldsParams() {
    customFieldParams = [];
    validStep = this.isValidStep();
  }

  private loadingDraggable(className: string) {
    let interactJs = InteractJS(`.${className}`);

    if (className === CustomFieldTypeEnum.TextField) {
      interactJs = interactJs.resizable({
        edges: {
          left: this.canResize,
          right: this.canResize,
          bottom: false,
          top: false
        },
        margin: 8,
        listeners: {
          move(event) {
            var target = event.target
            var x = (parseFloat(target.getAttribute('data-x')) || 0)
            var y = (parseFloat(target.getAttribute('data-y')) || 0)

            target.style.width = event.rect.width + 'px'
            target.style.height = event.rect.height + 'px'

            x += event.deltaRect.left
            y += event.deltaRect.top

            target.style.transform = 'translate(' + x + 'px,' + y + 'px)'

            target.setAttribute('data-x', x)
            target.setAttribute('data-y', y)

            onSize(event);
          }
        },
        modifiers: [
          InteractJS.modifiers.restrictEdges({
            outer: 'parent'
          }),
          InteractJS.modifiers.restrictSize({
            min: { width: 100, height: 25 }
          })
        ],
        inertia: true
      });
    } else if (className !== CustomFieldTypeEnum.CheckboxField) {
      interactJs = interactJs.resizable({
        edges: { left: this.canResize, right: this.canResize, bottom: this.canResize, top: this.canResize },
        listeners: {
          move(event) {
            var target = event.target
            var x = (parseFloat(target.getAttribute('data-x')) || 100)
            var y = (parseFloat(target.getAttribute('data-y')) || 0)

            target.style.width = event.rect.width + 'px'
            target.style.height = event.rect.height + 'px'

            x += event.deltaRect.left
            y += event.deltaRect.top

            target.style.transform = 'translate(' + x + 'px,' + y + 'px)'

            target.setAttribute('data-x', x)
            target.setAttribute('data-y', y)

            onSize(event);
          }
        },
        modifiers: [
          InteractJS.modifiers.restrictEdges({
            outer: 'parent'
          }),
          InteractJS.modifiers.restrictSize({
            min: { width: 50, height: 46 },
            max: { width: 300, height: 150 },
          })
        ],
        inertia: true
      });
    }

    interactJs
      .draggable({
        inertia: true,
        maxPerElement: 3,
        restrict: {
          elementRect: {
            top: 0,
            left: 0,
            bottom: 1,
            right: 1
          }
        },
        modifiers: [
          InteractJS.modifiers.restrictRect({
            restriction: 'parent',
            endOnly: true
          })
        ],
        onmove(event) {
          const target = event.target;
          const dataX = target.getAttribute('data-x');
          const dataY = target.getAttribute('data-y');
          const initialX = parseFloat(dataX) || 0;
          const initialY = parseFloat(dataY) || 0;

          const deltaX = event.dx;
          const deltaY = event.dy;

          const newX = initialX + deltaX;
          const newY = initialY + deltaY;

          target.style.transform = `translate(${newX}px, ${newY}px)`;

          target.setAttribute('data-x', newX);
          target.setAttribute('data-y', newY);
        },
      });

    function onSize(event: any) {
      const draggableElement = event.currentTarget;

      if (draggableElement.getAttribute('data-type') == 'signature') {
        const signatoryStamp = new SignatoryStamp();
        signatoryStamp.gId = draggableElement.dataset.gid;
        signatoryStamp.signatoryId = draggableElement.dataset.id;
        signatoryStamp.fileName = draggableElement.id;
        signatoryStamp.signaturePositionPixelX = parseFloat(draggableElement.getAttribute('data-x'));
        signatoryStamp.signaturePositionPixelY = parseFloat(draggableElement.getAttribute('data-y'));
        signatoryStamp.signatureSizePixelWidth = parseFloat(event.rect.width);
        signatoryStamp.signatureSizePixelHeight = parseFloat(event.rect.height);
        setSizeSignatureStamp(signatoryStamp);
      } else if (draggableElement.getAttribute('data-type') == 'rubric') {
        const rubricStamp = new RubricStamp();
        rubricStamp.fileName = draggableElement.id;
        rubricStamp.signatoryId = draggableElement.dataset.id;
        rubricStamp.gId = draggableElement.dataset.gid;
        rubricStamp.positionPixelX = parseFloat(draggableElement.getAttribute('data-x'));
        rubricStamp.positionPixelY = parseFloat(draggableElement.getAttribute('data-y'));
        rubricStamp.sizePixelWidth = parseFloat(event.rect.width);
        rubricStamp.sizePixelHeight = parseFloat(event.rect.height);
        setSizeRubricStamp(rubricStamp);
      } else {
        const customField = new FormCustomField();
        customField.gId = draggableElement.dataset.gid;
        customField.signatoryId = draggableElement.dataset.id;
        customField.fileName = draggableElement.id;
        customField.sizePixelWidth = parseFloat(event.rect.width);
        customField.sizePixelHeight = parseFloat(event.rect.height);
        customField.positionPixelX = parseFloat(draggableElement.getAttribute('data-x'));
        customField.positionPixelY = parseFloat(draggableElement.getAttribute('data-y'));
        setSizeCustomField(customField);
      }
    }

    function setSizeSignatureStamp(signatoryStamp: SignatoryStamp) {
      const index = stampParams.indexOf(
        stampParams.find((x) => x.signatoryId === signatoryStamp.signatoryId && x.fileName === signatoryStamp.fileName && x.gId == signatoryStamp.gId)
      );
      if (index >= 0) {
        const x = Math.round(((signatoryStamp.signatureSizePixelWidth / 3.779) + Number.EPSILON) * 100) / 100;
        const y = Math.round(((signatoryStamp.signatureSizePixelHeight / 3.779) + Number.EPSILON) * 100) / 100;

        stampParams[index].signaturePositionX = Math.round(((signatoryStamp.signaturePositionPixelX / 3.779) + Number.EPSILON) * 100) / 100;
        stampParams[index].signaturePositionY = Math.round(((signatoryStamp.signaturePositionPixelY / 3.779) + Number.EPSILON) * 100) / 100;
        stampParams[index].signaturePositionPixelX = signatoryStamp.signaturePositionPixelX;
        stampParams[index].signaturePositionPixelY = signatoryStamp.signaturePositionPixelY;
        stampParams[index].signatureSizePixelWidth = signatoryStamp.signatureSizePixelWidth;
        stampParams[index].signatureSizePixelHeight = signatoryStamp.signatureSizePixelHeight;
        stampParams[index].signatureSizeWidth = x;
        stampParams[index].signatureSizeHeight = y;
      }
    }

    function setSizeRubricStamp(rubricStamp: RubricStamp) {
      const index = rubricParams.indexOf(
        rubricParams.find((x) => x.signatoryId === rubricStamp.signatoryId && x.gId === rubricStamp.gId)
      );
      if (index >= 0) {
        const x = Math.round(((rubricStamp.sizePixelWidth / 3.779) + Number.EPSILON) * 100) / 100;
        const y = Math.round(((rubricStamp.sizePixelHeight / 3.779) + Number.EPSILON) * 100) / 100;

        rubricParams[index].positionX = Math.round(((rubricStamp.positionPixelX / 3.779) + Number.EPSILON) * 100) / 100;
        rubricParams[index].positionY = Math.round(((rubricStamp.positionPixelY / 3.779) + Number.EPSILON) * 100) / 100;
        rubricParams[index].positionPixelX = rubricStamp.positionPixelX;
        rubricParams[index].positionPixelY = rubricStamp.positionPixelY;
        rubricParams[index].sizePixelWidth = rubricStamp.sizePixelWidth;
        rubricParams[index].sizePixelHeight = rubricStamp.sizePixelHeight;
        rubricParams[index].sizeWidth = x;
        rubricParams[index].sizeHeight = y;
      }
    }

    function setSizeCustomField(customField: FormCustomField) {
      const index = customFieldParams.indexOf(
        customFieldParams.find((x) =>
          x.signatoryId === customField.signatoryId &&
          x.fileName === customField.fileName &&
          x.gId == customField.gId
        )
      );

      if (index >= 0) {
        const x = Math.round(((customField.sizePixelWidth / 3.779) + Number.EPSILON) * 100) / 100;
        const y = Math.round(((customField.sizePixelHeight / 3.779) + Number.EPSILON) * 100) / 100;

        customFieldParams[index].sizePixelWidth = customField.sizePixelWidth;
        customFieldParams[index].sizePixelHeight = customField.sizePixelHeight;
        customFieldParams[index].sizeWidth = x;
        customFieldParams[index].sizeHeight = y;
        customFieldParams[index].positionPixelX = customField.positionPixelX;
        customFieldParams[index].positionPixelY = customField.positionPixelY;
        customFieldParams[index].positionX = Math.round(((customField.positionPixelX / 3.779) + Number.EPSILON) * 100) / 100;
        customFieldParams[index].positionY = Math.round(((customField.positionPixelY / 3.779) + Number.EPSILON) * 100) / 100;
      }
    }
  }
  private loadingDropzone(className: string) {
    validStep = isValidStep();
    InteractJS(`.${className}`).dropzone({
      overlap: 0.75,
      ondragenter(event) {
        const draggableElement = event.relatedTarget;
        draggableElement.classList.remove('cannot-drop-out-document');
        draggableElement.setAttribute('out-document', 'false');
        draggableElement.classList.add('can-drop');

        if (draggableElement.className.indexOf('signature') >= 0) {
          const fileName = draggableElement.id;
          const signatoryId = draggableElement.getAttribute('data-id');
          if (!signatoryId) {
            return;
          }
          const stamp = stampParams.find((x) => x.fileName === fileName && x.signatoryId === signatoryId);
          if (stamp) {
            stamp.invalid = false;
          }

          validStep = isValidStep();
        } else if (draggableElement.className.indexOf('rubric') >= 0) {
          event.relatedTarget.classList.remove('cannot-drop-out-document');
          event.relatedTarget.setAttribute('valid', 'true');

          const fileName = draggableElement.id;
          const gId = draggableElement.getAttribute('data-gId') || draggableElement.getAttribute('data-gid');
          if (!gId) {
            return;
          }

          const rubric = rubricParams.find((x) => x.fileName === fileName && x.gId === gId);
          if (rubric) {
            rubric.invalid = false;
          }
          validStep = rubricParams.every((x) => x.invalid === false);
        } else if (draggableElement.className.indexOf(CustomFieldTypeEnum.TextField) >= 0 ||
          draggableElement.className.indexOf(CustomFieldTypeEnum.CheckboxField) >= 0) {
          validStep = isValidStep();
        }
      },
      ondragleave(event) {
        const draggableElement = event.relatedTarget;
        draggableElement.classList.remove('can-drop');
        draggableElement.setAttribute('out-document', 'true');
        draggableElement.classList.add('cannot-drop-out-document');
        draggableElement.setAttribute('valid', 'false');

        validStep = isValidStep();
      },
      ondrop(event) {
        onDrop(event);
      }
    });

    function onDrop(event) {
      let signaturesRubrics = [];
      let customFieldsOnPage;

      const draggableElement = event.relatedTarget;
      event.relatedTarget.classList.remove('can-drop');
      const dataType = draggableElement.getAttribute('data-type');
      const fileName = draggableElement.id;
      const signatoryId = draggableElement.getAttribute('data-id');
      const gId = draggableElement.getAttribute('data-gId') || draggableElement.getAttribute('data-gid');
      const targetPage = event.currentTarget?.dataset?.pageNumber;
      const previousPage = getElementCurrentPage(dataType, fileName, signatoryId, gId);
      const affectedPages = new Set<string>();
      if (targetPage) {
        affectedPages.add(targetPage);
      }
      if (previousPage) {
        affectedPages.add(previousPage);
      }

      const signatures = document.getElementsByClassName('signature');
      const rubrics = document.getElementsByClassName('rubric');
      const textFields = document.getElementsByClassName(CustomFieldTypeEnum.TextField);
      const checkboxFields = document.getElementsByClassName(CustomFieldTypeEnum.CheckboxField);

      customFieldsOnPage = Array.prototype.slice.apply(textFields);
      customFieldsOnPage = customFieldsOnPage.concat(Array.from(checkboxFields));

      signaturesRubrics = Array.prototype.slice.apply(signatures);
      signaturesRubrics = signaturesRubrics.concat(Array.from(rubrics));
      signaturesRubrics = signaturesRubrics.concat(Array.from(textFields));
      signaturesRubrics = signaturesRubrics.concat(Array.from(checkboxFields));

      if (draggableElement.getAttribute('data-type') == 'signature') {

        const signatoryStamp = new SignatoryStamp();
        signatoryStamp.signaturePositionPage = event.currentTarget.dataset.pageNumber;
        signatoryStamp.gId = draggableElement.dataset.gid;
        signatoryStamp.signatoryId = draggableElement.dataset.id;
        signatoryStamp.fileName = draggableElement.id;
        signatoryStamp.signaturePositionX = parseFloat(draggableElement.getAttribute('data-x'));
        signatoryStamp.signaturePositionY = parseFloat(draggableElement.getAttribute('data-y'));
        setSignatoryStamp(signatoryStamp);

      } else if (draggableElement.getAttribute('data-type') == 'rubric') {
        const rubricStamp = new RubricStamp();
        rubricStamp.fileName = draggableElement.id;
        rubricStamp.signatoryId = draggableElement.dataset.id;
        rubricStamp.page = event.currentTarget.dataset.pageNumber;
        rubricStamp.gId = draggableElement.dataset.gid;
        rubricStamp.positionX = parseFloat(draggableElement.getAttribute('data-x'));;
        rubricStamp.positionY = parseFloat(draggableElement.getAttribute('data-y'));;
        setSignatoryRubric(rubricStamp);
      } else {
        const customField = new FormCustomField();
        customField.fileName = draggableElement.id;
        customField.signatoryId = draggableElement.dataset.id;
        customField.page = event.currentTarget.dataset.pageNumber;
        customField.gId = draggableElement.dataset.gid;
        customField.positionX = parseFloat(draggableElement.getAttribute('data-x'));
        customField.positionY = parseFloat(draggableElement.getAttribute('data-y'));
        setCustomField(customField);
      }

      const dropzonesByPage = getDropzonesByPage();
      const validationElements = signaturesRubrics
        .map((element: Element) => createPlacementValidationElement(element))
        .filter((element): element is PlacementValidationElement => element !== null);

      const pagesToValidate = affectedPages.size > 0 ? affectedPages : undefined;
      const validityByKey = calculatePlacementValidity(validationElements, _fileName, dropzonesByPage, pagesToValidate);

      for (let index = 0; index < signaturesRubrics.length; index++) {
        const currentElement = signaturesRubrics[index];
        const key = getElementKey(currentElement);
        if (!key || !validityByKey.has(key)) {
          continue;
        }

        currentElement.setAttribute('valid', validityByKey.get(key) ? 'true' : 'false');
      }

      setInvalidSignatures(signatures, pagesToValidate);
      setInvalidRubrics(rubrics, pagesToValidate);
      setInvalidCustomFields(customFieldsOnPage, pagesToValidate);
    }

    function getElementCurrentPage(
      dataType: string | null,
      fileName: string,
      signatoryId: string | null,
      gId: string | null
    ): string | null {
      if (!fileName || !signatoryId || !gId) {
        return null;
      }

      if (dataType === 'signature') {
        const signatoryStamp = stampParams.find(
          (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
        );
        return signatoryStamp?.signaturePositionPage || null;
      }

      if (dataType === 'rubric') {
        const rubricStamp = rubricParams.find(
          (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
        );
        return rubricStamp?.page || null;
      }

      const customField = customFieldParams.find(
        (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
      );
      return customField?.page || null;
    }

    function setSignatoryStamp(signatoryStamp: any) {
      const index = stampParams.indexOf(
        stampParams.find((x) => x.signatoryId === signatoryStamp.signatoryId && x.fileName === signatoryStamp.fileName && x.gId == signatoryStamp.gId)
      );
      if (index >= 0) {
        stampParams[index].signaturePositionPage = signatoryStamp.signaturePositionPage;
        const x = Math.round(((signatoryStamp.signaturePositionX / 3.779) + Number.EPSILON) * 100) / 100;
        const y = Math.round(((signatoryStamp.signaturePositionY / 3.779) + Number.EPSILON) * 100) / 100;

        stampParams[index].signaturePositionPixelX = signatoryStamp.signaturePositionX;
        stampParams[index].signaturePositionPixelY = signatoryStamp.signaturePositionY;
        stampParams[index].signaturePositionX = x;
        stampParams[index].signaturePositionY = y;
      }
    }

    function setSignatoryRubric(signatoryRubric: RubricStamp) {
      const index = rubricParams.indexOf(
        rubricParams.find((x) => x.signatoryId === signatoryRubric.signatoryId && x.gId === signatoryRubric.gId)
      );
      if (index >= 0) {
        rubricParams[index].page = signatoryRubric.page;
        const x = Math.round((signatoryRubric.positionX / 3.779 + Number.EPSILON) * 100) / 100;
        const y = Math.round((signatoryRubric.positionY / 3.779 + Number.EPSILON) * 100) / 100;

        rubricParams[index].positionPixelX = signatoryRubric.positionX;
        rubricParams[index].positionPixelY = signatoryRubric.positionY;
        rubricParams[index].positionX = x;
        rubricParams[index].positionY = y;
      }
    }

    function setCustomField(customField: FormCustomField) {
      const index = customFieldParams.indexOf(
        customFieldParams.find((x) => x.signatoryId === customField.signatoryId && x.gId === customField.gId)
      );

      if (index >= 0) {
        customFieldParams[index].page = customField.page;
        const x = Math.round((customField.positionX / 3.779 + Number.EPSILON) * 100) / 100;
        const y = Math.round((customField.positionY / 3.779 + Number.EPSILON) * 100) / 100;

        customFieldParams[index].positionPixelX = customField.positionX;
        customFieldParams[index].positionPixelY = customField.positionY;
        customFieldParams[index].positionX = x;
        customFieldParams[index].positionY = y;
      }
    }

    function createPlacementValidationElement(element: Element): PlacementValidationElement | null {
      const fileName = element.id;
      const signatoryId = element.getAttribute('data-id');
      const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');
      const dataType = element.getAttribute('data-type');

      if (!fileName || !signatoryId || !gId) {
        return null;
      }

      const page = resolveElementPage(dataType, fileName, signatoryId, gId);
      if (!page) {
        return null;
      }

      const type: PlacementElementType = dataType === 'signature'
        ? 'signature'
        : (dataType === 'rubric' ? 'rubric' : 'customField');

      return {
        key: buildElementKey(fileName, signatoryId, gId),
        fileName,
        page,
        type,
        rect: element.getBoundingClientRect() as DOMRect,
        height: (element as HTMLElement).clientHeight
      };
    }

    function resolveElementPage(dataType: string | null, fileName: string, signatoryId: string, gId: string): string | null {
      if (dataType === 'signature') {
        const signatoryStamp = stampParams.find(
          (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
        );
        return signatoryStamp?.signaturePositionPage || null;
      }

      if (dataType === 'rubric') {
        const rubricStamp = rubricParams.find(
          (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
        );
        return rubricStamp?.page || null;
      }

      const customField = customFieldParams.find(
        (x) => x.signatoryId === signatoryId && x.fileName === fileName && x.gId == gId
      );
      return customField?.page || null;
    }

    function getDropzonesByPage(): Map<string, DOMRect> {
      const dropzonesByPage = new Map<string, DOMRect>();
      const dropzones = Array.from(document.getElementsByClassName('page dropzone'));

      for (let index = 0; index < dropzones.length; index++) {
        const dropzone = dropzones[index] as HTMLElement;
        const page = dropzone.getAttribute('data-page-number');
        if (!page) {
          continue;
        }

        dropzonesByPage.set(page, dropzone.getBoundingClientRect() as DOMRect);
      }

      return dropzonesByPage;
    }

    function getElementKey(element: Element): string | null {
      const fileName = element.id;
      const signatoryId = element.getAttribute('data-id');
      const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

      if (!fileName || !signatoryId || !gId) {
        return null;
      }

      return buildElementKey(fileName, signatoryId, gId);
    }

    function buildElementKey(fileName: string, signatoryId: string, gId: string): string {
      return `${fileName}::${signatoryId}::${gId}`;
    }

    function setInvalidSignatures(signatures: HTMLCollectionOf<Element>, targetPages?: Set<string>) {
      for (let index = 0; index < signatures.length; index++) {
        const element = signatures[index];
        const fileName = element.id;
        const signatoryId = element.getAttribute('data-id');
        const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

        if (!signatoryId || !gId) {
          continue;
        }

        const stampSignatory = stampParams.find(
          (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
        );

        if (!stampSignatory) {
          continue;
        }

        if (targetPages && targetPages.size > 0 && !targetPages.has(stampSignatory.signaturePositionPage)) {
          continue;
        }

        if (element.getAttribute('valid') === 'false') {
          element.classList.add('cannot-drop');
          stampSignatory.invalid = true;
        } else {
          element.classList.remove('cannot-drop');
          stampSignatory.invalid = false;
        }

        if (element.getAttribute('out-document') === 'true') {
          stampSignatory.invalid = true;
        }
      }
      validStep = isValidStep();
    }

    function setInvalidRubrics(rubrics: HTMLCollectionOf<Element>, targetPages?: Set<string>) {
      for (let index = 0; index < rubrics.length; index++) {
        const element = rubrics[index];
        const fileName = element.id;
        const signatoryId = element.getAttribute('data-id');
        const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

        if (!signatoryId || !gId) {
          continue;
        }

        const rubricSignatory = rubricParams.find(
          (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId === gId
        );

        if (rubricSignatory === undefined) {
          element.setAttribute('valid', 'false');
          element.classList.add('cannot-drop-out-document');
          continue;
        }

        if (targetPages && targetPages.size > 0 && !targetPages.has(rubricSignatory.page)) {
          continue;
        }

        if (element.getAttribute('valid') === 'false') {
          element.classList.add('cannot-drop-out-document');
          rubricSignatory.invalid = true;
        } else if (element.getAttribute('out-document') === 'true') {
          rubricSignatory.invalid = true;
        } else {
          element.classList.remove('cannot-drop-out-document');
          rubricSignatory.invalid = false;
          element.setAttribute('valid', 'true');
        }
      }

      validStep = isValidStep();
    }

    function setInvalidCustomFields(componentFields: HTMLCollectionOf<Element>, targetPages?: Set<string>) {
      for (let index = 0; index < componentFields.length; index++) {
        const element = componentFields[index];
        const fileName = element.id;
        const signatoryId = element.getAttribute('data-id');
        const gId = element.getAttribute('data-gId') || element.getAttribute('data-gid');

        if (!signatoryId || !gId) {
          continue;
        }

        const customField = customFieldParams.find(
          (x) => x.fileName === fileName && x.signatoryId === signatoryId && x.gId == gId
        );

        if (!customField) {
          continue;
        }

        if (targetPages && targetPages.size > 0 && !targetPages.has(customField.page)) {
          continue;
        }

        if (element.getAttribute('valid') === 'false') {
          element.classList.add('cannot-drop');
          customField.invalid = true;
        } else {
          element.classList.remove('cannot-drop');
          customField.invalid = false;
        }

        if (element.getAttribute('out-document') === 'true') {
          customField.invalid = true;
        }
      }

      validStep = isValidStep();
    }

    function isValidStep(): boolean {
      return stampParams.every((x) => x.invalid === false)
        && rubricParams.every((x) => x.invalid === false)
        && customFieldParams.every((x) => x.invalid === false);
    }
  }
}
